/* import __COLOCATED_TEMPLATE__ from './list.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { ajaxDelete, get } from 'embercom/lib/ajax';
import { submitOAuthForm } from 'embercom/lib/oauth/form-submission';
import { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type NotificationsService from 'embercom/services/notifications-service';
import { hasFeature } from 'embercom/helpers/has-feature';
import type Token from 'embercom/models/custom-authentication/token';

interface Signature {
  Args: {
    customAuthenticationTokens: Token[];
  };
}

export default class List extends Component<Signature> {
  @service declare router: Router;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  @tracked customAuthenticationTokens = this.args.customAuthenticationTokens;
  @tracked tokenToDelete: Token | null = null;
  @tracked showDeleteModal = false;

  get providers() {
    return [
      {
        id: 'salesforce',
        icon: 'authentication/salesforce',
        titleKey: 'settings.custom-authentication-tokens.providers.salesforce',
        onClick: () => this.connectOAuth('salesforce', false),
      },
      {
        id: 'salesforce-sandbox',
        icon: 'authentication/salesforce',
        titleKey: 'settings.custom-authentication-tokens.providers.salesforce-sandbox',
        onClick: () => this.connectOAuth('salesforce', true),
      },
      {
        id: 'hubspot',
        icon: 'authentication/hubspot',
        titleKey: 'settings.custom-authentication-tokens.providers.hubspot',
        onClick: () => this.connectOAuth('hubspot', false),
      },
      {
        id: 'attio',
        icon: 'authentication/attio',
        titleKey: 'settings.custom-authentication-tokens.providers.attio',
        onClick: () => this.connectOAuth('attio', false),
      },
      {
        id: 'custom',
        icon: 'authentication/new-token',
        titleKey: 'settings.custom-authentication-tokens.providers.custom',
        onClick: () => this.selectCustomToken(),
      },
    ];
  }

  get tableColumns() {
    return [
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.name'),
        valuePath: 'name',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.type'),
        valuePath: 'tokenType',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.matched-by-domain'),
        valuePath: 'domain',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.created-by'),
        valuePath: 'person.createdBy',
        type: 'avatar-with-text',
      },
      {
        label: this.intl.t('settings.custom-authentication-tokens.list.columns.updated-by'),
        valuePath: 'person.updatedBy',
        type: 'avatar-with-text',
      },
      {
        label: '',
        valuePath: 'actions',
      },
    ].filter((column) => {
      if (column.valuePath === 'domain') {
        return hasFeature('domain-based-token-selection', this.appService);
      }
      return true;
    });
  }

  @action
  openDeleteModal(token: Token) {
    this.tokenToDelete = token;
    this.showDeleteModal = true;
  }

  @action
  closeDeleteModal() {
    this.tokenToDelete = null;
    this.showDeleteModal = false;
  }

  @action
  async deleteToken() {
    if (!this.tokenToDelete) {
      return;
    }

    try {
      if (this.tokenToDelete.isCalendlyToken) {
        // Use the special Calendly remove_integration endpoint
        await ajaxDelete(`/ember/sdr/calendly/remove_integration`, {
          app_id: this.appService.app.id,
        });
      } else {
        await ajaxDelete(`/ember/custom_authentication/tokens/${this.tokenToDelete.id}`, {
          app_id: this.appService.app.id,
        });
      }

      this.customAuthenticationTokens = this.customAuthenticationTokens.filter(
        (token: Token) => token.id !== this.tokenToDelete?.id,
      );

      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.custom-authentication-tokens.delete-modal.success-message'),
      );

      this.closeDeleteModal();
    } catch (e: unknown) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t('settings.custom-authentication-tokens.delete-modal.error-message'),
      });
    }
  }

  @action
  selectCustomToken() {
    this.router.transitionTo('apps.app.settings.app-settings.authentication.new');
  }

  @action
  async connectOAuth(provider: 'salesforce' | 'hubspot' | 'attio', isSandbox = false) {
    try {
      let url = `/ember/custom_authentication/oauth/${provider}/connect?app_id=${this.appService.app.id}&sandbox=${isSandbox}`;
      let response = await get(url);
      submitOAuthForm(response.authorization_url);
    } catch (error: unknown) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('settings.custom-authentication-tokens.oauth-connect-error'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::AppSettings::Authentication::List': typeof List;
    'new-settings/app-settings/authentication/list': typeof List;
  }
}
